import { SEOHead } from "@/components/SEOHead";
import { HeaderNavigation } from "@/components/HeaderNavigation";
import { FooterSEO } from "@/components/FooterSEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Building2, MapPin, Mail, Award, ShieldCheck, Users, CheckCircle2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import gurkanImage from "@/assets/gurkan-sonmez.jpg";

export default function AboutUs() {
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": "Corner Gayrimenkul",
    "description": "Corner Gayrimenkul, Adana merkezli gayrimenkul danışmanlık firmasıdır. Seyhan ve Çukurova başta olmak üzere konut, ticari gayrimenkul, arsa, satış, kiralama, değerleme ve yatırım danışmanlığı alanlarında hizmet verir.",
    "url": "https://www.cornergayrimenkul.com.tr",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Gürselpaşa Mahallesi, Aliya İzzetbegoviç Bulvarı",
      "addressLocality": "Seyhan",
      "addressRegion": "Adana",
      "postalCode": "01200",
      "addressCountry": "TR"
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col justify-between">
      <SEOHead
        title="Hakkımızda | Corner Gayrimenkul Adana"
        description="Corner Gayrimenkul, Adana merkezli gayrimenkul danışmanlık firmasıdır. Seyhan, Çukurova ve Gürselpaşa bölgesinde profesyonel emlak, değerleme ve yatırım hizmetleri."
        canonicalPath="/hakkimizda"
        schemaJson={aboutSchema}
      />
      <HeaderNavigation />

      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
        <Breadcrumbs items={[{ label: "Hakkımızda" }]} />

        {/* Hero Banner */}
        <div className="relative rounded-3xl overflow-hidden bg-slate-900 text-white p-8 sm:p-12 mb-12 shadow-xl border border-slate-800">
          <div className="max-w-3xl space-y-4">
            <span className="px-3 py-1 rounded-full bg-primary/20 text-primary border border-primary/30 text-xs font-bold uppercase tracking-wider">
              Kurumsal Profil
            </span>
            <h1 className="text-3xl sm:text-5xl font-black tracking-tight leading-tight">
              Adana'nın Güvenilir Gayrimenkul Çözüm Ortağı
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed">
              <strong>Corner Gayrimenkul</strong>, Adana merkezli profesyonel gayrimenkul danışmanlık firmasıdır. Seyhan ve Çukurova başta olmak üzere konut, ticari gayrimenkul, arsa, satış, kiralama, değerleme ve yatırım danışmanlığı alanlarında şeffaf ve güvenilir hizmet sunar.
            </p>
          </div>
        </div>

        {/* Core Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="p-6 bg-card border border-border">
            <Award className="h-8 w-8 text-primary mb-3" />
            <h3 className="font-bold text-lg text-foreground mb-2">Saha Deneyimi</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Mülkün konumu, kullanım durumu ve belgelerini birlikte inceleyerek değerlendirme sürecini planlıyoruz.
            </p>
          </Card>

          <Card className="p-6 bg-card border border-border">
            <ShieldCheck className="h-8 w-8 text-primary mb-3" />
            <h3 className="font-bold text-lg text-foreground mb-2">Şeffaf Süreç Yönetimi</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              İşlem adımlarını, gerekli belgeleri ve tarafların beklentilerini görüşme sırasında açıkça ele alıyoruz.
            </p>
          </Card>

          <Card className="p-6 bg-card border border-border">
            <Users className="h-8 w-8 text-primary mb-3" />
            <h3 className="font-bold text-lg text-foreground mb-2">Geniş Alıcı Ağı</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Web sitemiz, Sahibinden mağazamız ve dijital pazarlama araçlarımızla gayrimenkulünüzü en hızlı alıcıyla buluşturuyoruz.
            </p>
          </Card>
        </div>

        {/* Founder & Agent Section */}
        <div className="bg-card rounded-3xl p-8 sm:p-12 border border-border mb-12 flex flex-col md:flex-row items-center gap-8">
          <div className="w-40 h-40 rounded-full overflow-hidden shrink-0 border-4 border-primary/20 shadow-xl">
            <img src={gurkanImage} alt="Gürkan Sönmez" className="w-full h-full object-cover" />
          </div>
          <div className="space-y-3 text-center md:text-left">
            <span className="text-xs font-bold text-primary uppercase tracking-wider">KURUCU & GAYRİMENKUL DANIŞMANI</span>
            <h2 className="text-3xl font-extrabold text-foreground">Gürkan Sönmez</h2>
            <p className="text-muted-foreground text-base leading-relaxed max-w-2xl">
              "Amacımız sadece bir emlak ilanı yayınlamak değil; Adana genelinde gayrimenkul alıcı ve satıcılarına saha verilerine dayalı dürüst, etik ve nitelikli danışmanlık hizmeti sunmaktır."
            </p>
            <div className="pt-2 text-sm font-semibold text-foreground flex flex-wrap justify-center md:justify-start gap-4 items-center">
              <span>📍 Gürselpaşa Ofisi</span>
              <a href="https://wa.me/905335487801" target="_blank" rel="noreferrer" className="text-green-600 hover:underline font-bold flex items-center gap-1">💬 WhatsApp</a>
              <a href="mailto:info@cornergayrimenkul.com.tr" className="hover:underline">✉️ info@cornergayrimenkul.com.tr</a>
            </div>
          </div>
        </div>
      </main>

      <FooterSEO />
    </div>
  );
}
